import { useEffect, useState, type JSX } from 'react';
import {
  DRONE_AVAILABILITY_STATUSES,
  DRONE_DOWNLINK_TYPES,
  DRONE_PLATFORM_CATEGORIES,
  DRONE_SENSOR_TYPES,
  type DroneAvailabilityStatus,
  type DroneDownlinkType,
  type DronePlatformCategory,
  type DroneSensorType,
} from '@tsr/shared-types';
import {
  createOperatorAsset,
  fetchOperatorAssets,
  updateOperatorAsset,
  updateOperatorAssetAvailability,
  type DroneAssetView,
} from '../api';
import {
  Chip,
  Field,
  PortalSection,
  ghostButtonClass,
  inputClass,
  primaryButtonClass,
} from './portalUi';

/**
 * Fleet registration + availability (Drone D1 #318) — the operator's OWN drone assets:
 * register against the #317 capability taxonomy, set the sensor loadout, toggle
 * availability / on-call, and retire. Every call is server-scoped to the operator's agency.
 */
export function FleetSection(): JSX.Element {
  const [assets, setAssets] = useState<DroneAssetView[]>([]);
  const [name, setName] = useState('');
  const [tail, setTail] = useState('');
  const [platform, setPlatform] = useState<DronePlatformCategory>(DRONE_PLATFORM_CATEGORIES[0]);
  const [downlink, setDownlink] = useState<DroneDownlinkType | ''>('');
  const [sensors, setSensors] = useState<Set<DroneSensorType>>(new Set());
  const [radius, setRadius] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reload(): Promise<void> {
    try {
      setAssets(await fetchOperatorAssets());
    } catch {
      /* keep current */
    }
  }
  useEffect(() => {
    void reload();
  }, []);

  function toggleSensor(s: DroneSensorType): void {
    setSensors((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  async function register(): Promise<void> {
    if (name.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await createOperatorAsset({
        name: name.trim(),
        tail_number: tail.trim() || null,
        platform_category: platform,
        downlink_type: downlink || null,
        service_radius_km: radius ? Number(radius) : null,
        sensors: [...sensors],
      });
      setName('');
      setTail('');
      setDownlink('');
      setRadius('');
      setSensors(new Set());
      await reload();
    } catch {
      setError('Could not register the asset.');
    } finally {
      setBusy(false);
    }
  }

  async function setAvailability(id: string, patch: { availability_status?: DroneAvailabilityStatus; on_call?: boolean }): Promise<void> {
    try {
      await updateOperatorAssetAvailability(id, patch);
      await reload();
    } catch {
      setError('Could not update availability.');
    }
  }

  async function retire(id: string): Promise<void> {
    try {
      await updateOperatorAsset(id, { status: 'retired' });
      await reload();
    } catch {
      setError('Could not retire the asset.');
    }
  }

  const active = assets.filter((a) => a.status !== 'retired');

  return (
    <PortalSection title="Fleet & availability" testId="portal-fleet">
      {active.length === 0 ? (
        <p className="text-sm text-slate-500">No active aircraft registered yet.</p>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="asset-list">
          {active.map((a) => (
            <li key={a.id} className="flex flex-col gap-2 rounded-md border border-slate-800 bg-slate-950/40 p-3" data-testid="asset-row">
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-slate-100">
                    {a.name}
                    {a.tail_number ? <span className="ml-2 text-xs text-slate-500">{a.tail_number}</span> : null}
                  </span>
                  <span className="text-[11px] uppercase tracking-wide text-slate-500">
                    {a.platform_category}
                    {a.downlink_type ? ` · ${a.downlink_type}` : ''}
                    {a.service_radius_km ? ` · ${a.service_radius_km} km` : ''}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {a.sensors.map((s) => (
                      <Chip key={s}>{s}</Chip>
                    ))}
                  </div>
                </div>
                <button type="button" className={ghostButtonClass} data-testid="asset-retire" onClick={() => void retire(a.id)}>
                  Retire
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 pt-2">
                <select
                  data-testid="asset-availability"
                  aria-label={`availability for ${a.name}`}
                  className={inputClass}
                  value={a.availability_status}
                  onChange={(e) => void setAvailability(a.id, { availability_status: e.target.value as DroneAvailabilityStatus })}
                >
                  {DRONE_AVAILABILITY_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid="asset-oncall"
                  aria-pressed={a.on_call}
                  className={`rounded-md border px-2.5 py-1.5 text-xs ${
                    a.on_call
                      ? 'border-amber-500/50 bg-amber-500/10 text-amber-200'
                      : 'border-slate-700 text-slate-400 hover:text-slate-200'
                  }`}
                  onClick={() => void setAvailability(a.id, { on_call: !a.on_call })}
                >
                  {a.on_call ? 'On call' : 'Off call'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-1 grid grid-cols-1 gap-2 border-t border-slate-800 pt-3 sm:grid-cols-2">
        <Field label="Aircraft name">
          <input data-testid="asset-name" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Heron VTOL" />
        </Field>
        <Field label="Tail number (optional)">
          <input className={inputClass} value={tail} onChange={(e) => setTail(e.target.value)} placeholder="C-XXXX" />
        </Field>
        <Field label="Platform">
          <select
            data-testid="asset-platform"
            className={inputClass}
            value={platform}
            onChange={(e) => setPlatform(e.target.value as DronePlatformCategory)}
          >
            {DRONE_PLATFORM_CATEGORIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Downlink (optional)">
          <select className={inputClass} value={downlink} onChange={(e) => setDownlink(e.target.value as DroneDownlinkType | '')}>
            <option value="">none</option>
            {DRONE_DOWNLINK_TYPES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Service radius km (optional)">
          <input className={inputClass} type="number" min="0" value={radius} onChange={(e) => setRadius(e.target.value)} placeholder="120" />
        </Field>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Sensors</span>
        <div className="flex flex-wrap gap-1.5" data-testid="asset-sensor-picker">
          {DRONE_SENSOR_TYPES.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={sensors.has(s)}
              onClick={() => toggleSensor(s)}
              className={`rounded px-2 py-1 text-[10px] uppercase tracking-wide ${
                sensors.has(s) ? 'bg-slate-200 text-slate-900' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="asset-register"
          className={primaryButtonClass}
          disabled={busy || name.trim().length === 0}
          onClick={() => void register()}
        >
          {busy ? 'Registering…' : 'Register aircraft'}
        </button>
        {error ? <span className="text-xs text-amber-300">{error}</span> : null}
      </div>
    </PortalSection>
  );
}
